<script setup lang="ts" generic="T extends string | number">
/**
 * THE kit's sort picker for a toolbar — „Zuletzt geändert ▾“. A button that
 * names the current order in full (no truncated native select) and opens the
 * kit menu with one tick entry per order. Same height scale and frame as the
 * search field and `NxToggleGroup` beside it, so a toolbar row lines up.
 *
 * `label` names the control („Sortierung“) for screen readers and the tooltip;
 * the button itself shows the chosen option's label.
 */
import ArrowDownUp from 'lucide-vue-next/dist/esm/icons/arrow-down-up.js'; import ChevronDown from 'lucide-vue-next/dist/esm/icons/chevron-down.js';
import { computed } from 'vue';
import type { NxButtonSize } from '../Button';
import NxContextMenuItem from '../ContextMenu/NxContextMenuItem.vue';
import NxMenuButton from './NxMenuButton.vue';

export interface NxSortMenuOption<V extends string | number> {
  value: V;
  label: string;
}

const props = withDefaults(
  defineProps<{
    options: NxSortMenuOption<T>[];
    /** Accessible name and tooltip. Defaults to „Sortierung“. */
    label?: string;
    /** Height scale, matches NxButton / NxToggleGroup. Defaults to `'s'`. */
    height?: NxButtonSize;
    disabled?: boolean;
  }>(),
  { label: 'Sortierung', height: 's', disabled: false }
);

const model = defineModel<T>({ required: true });

const current = computed(() => props.options.find((opt) => opt.value === model.value));
</script>

<template>
  <NxMenuButton
    :label="props.label"
    :height="props.height"
    :disabled="props.disabled"
    placement="bottom-end"
    class="nx-sort-menu"
  >
    <template #trigger="{ open, attrs }">
      <button
        v-bind="attrs"
        type="button"
        class="nx-sort-menu__trigger"
        :class="[`height-${props.height}`, { 'is-open': open }]"
        :disabled="props.disabled"
        :title="props.label"
        :aria-label="current ? `${props.label}: ${current.label}` : props.label"
      >
        <ArrowDownUp class="nx-sort-menu__icon nxt-icon-sm" aria-hidden="true" />
        <span class="nx-sort-menu__value">{{ current?.label ?? props.label }}</span>
        <ChevronDown class="nx-sort-menu__chevron nxt-icon-sm" aria-hidden="true" />
      </button>
    </template>
    <NxContextMenuItem
      v-for="opt in props.options"
      :key="String(opt.value)"
      :checked="opt.value === model"
      @select="model = opt.value"
    >
      {{ opt.label }}
    </NxContextMenuItem>
  </NxMenuButton>
</template>

<style scoped>
.nx-sort-menu__trigger {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  /* Same height as a field or button of this `height`: 1lh + 2 × space-xs + border. */
  height: calc(1lh + 2 * var(--nxt-space-xs) + 2 * var(--nxt-hairline));
  padding-inline: var(--nxt-space-sm);
  font-family: inherit;
  font-size: var(--tg-font);
  font-weight: var(--nxt-font-weight-medium);
  line-height: var(--nxt-line-height-control);
  white-space: nowrap;
  color: var(--nxt-fg);
  /* The input fill, like the search field and NxToggleGroup's track. */
  background: var(--nxt-input-bg);
  border: var(--nxt-hairline) solid var(--nxt-input-border);
  border-radius: var(--tg-radius);
  cursor: pointer;
  transition:
    border-color 0.15s,
    background-color 0.15s;
}

/* Font and radius per height step, as in NxToggleGroup. */
.nx-sort-menu__trigger.height-xs {
  --tg-font: var(--nxt-font-size-xs);
  --tg-radius: var(--nxt-radius-md);
}
.nx-sort-menu__trigger.height-s {
  --tg-font: var(--nxt-font-size-sm);
  --tg-radius: var(--nxt-radius-md);
}
.nx-sort-menu__trigger.height-m {
  --tg-font: var(--nxt-font-size-md);
  --tg-radius: var(--nxt-radius-lg);
}
.nx-sort-menu__trigger.height-l,
.nx-sort-menu__trigger.height-xl {
  --tg-font: var(--nxt-font-size-lg);
  --tg-radius: var(--nxt-radius-lg);
}

.nx-sort-menu__trigger:hover:not(:disabled),
.nx-sort-menu__trigger.is-open {
  background: var(--nxt-input-bg-hover);
  border-color: var(--nxt-input-border-hover);
}
.nx-sort-menu__trigger:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: calc(var(--nxt-hairline) * 2);
}
.nx-sort-menu__trigger:disabled {
  cursor: not-allowed;
  color: var(--nxt-disabled-fg);
}

.nx-sort-menu__icon,
.nx-sort-menu__chevron {
  flex: 0 0 auto;
  color: var(--nxt-fg-muted);
}
.nx-sort-menu__chevron {
  transition: transform 0.15s;
}
.nx-sort-menu__trigger.is-open .nx-sort-menu__chevron {
  transform: rotate(180deg);
}
</style>
